import {
  Box,
  Container,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Paper,
  Fade,
  IconButton,
  Tooltip,
  Collapse
} from '@material-ui/core'
import { useEffect, useState, useRef } from 'react'

import { GrNext, GrPrevious } from 'react-icons/gr'
import { FiMinus } from 'react-icons/fi'
import { Button } from 'src/components/ui/Button'
import { useQueryParams } from 'src/hooks/useQuery'
import UBox from '../ui/Ubox'
import { salvarPendenciaPacoteInteligente } from 'src/api/pacoteInteligente'
import { showNotificationErrorAPI } from 'src/core/helpers/notification'
import useAuth from 'src/hooks/useAuth'
import { SynPerfis } from 'src/core/consts/synPerfis'

const useStyles = makeStyles(theme => ({
  stepContent: {
    padding: theme.spacing(0, 0, 4, 0),
    minHeight: 400
  },
  cursorPointer: {
    cursor: 'pointer'
  },
  noMarginTop: { padding: '0px 24px 112px 24px !important' },
  [theme.breakpoints.down('sm')]: {
    stepContent: {
      padding: theme.spacing(3, 1.5, 14, 1.5),
      minHeight: '100vh'
    },
    noMarginTop: { padding: '0px 16px 112px 16px !important' },
    buttons: {
      position: 'fixed',
      padding: theme.spacing(2, 3),
      bottom: 0,
      margin: 0,
      left: 0,
      width: '100vw',
      borderTop: `1px solid ${theme.palette.grey[200]}`,
      backgroundColor: '#fff',
      zIndex: 10
    }
  }
}))

const PendenciasBox = ({ pacoteId, etapa, pendenciasOriginal = [], onPendenciaChange }: any) => {
  const { perfilAtual } = useAuth()
  const podeMarcarPendencias = [
    SynPerfis.OPERACIONAL,
    SynPerfis.SUPER_ADMIN,
    SynPerfis.ADMIN_HOSPITAL,
    SynPerfis.OPERACIONAL_HOSPITAL
  ].includes(perfilAtual)

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const pendenciaInicial = pendenciasOriginal?.find((p: any) => p.etapa === etapa)
  const [possuiPendencia, setPossuiPendencia] = useState(pendenciaInicial?.possuiPendencia || false)
  const [comentario, setComentario] = useState(pendenciaInicial?.comentario || '')
  const [loading, setLoading] = useState(false)
  const [salvo, setSalvo] = useState(false)
  const [minimizado, setMinimizado] = useState(false)
  const timer = useRef<any>(null)
  const timerSalvo = useRef<any>(null)

  if (!podeMarcarPendencias) {
    return null
  }

  useEffect(() => {
    const pend = pendenciasOriginal?.find((p: any) => p.etapa === etapa)
    setPossuiPendencia(pend?.possuiPendencia || false)
    setComentario(pend?.comentario || '')
  }, [etapa, pendenciasOriginal])

  const triggerSave = (checked: boolean, texto: string) => {
    if (!pacoteId) return
    setLoading(true)
    setSalvo(false)
    if (timerSalvo.current) clearTimeout(timerSalvo.current)

    salvarPendenciaPacoteInteligente(pacoteId, {
      etapa,
      possuiPendencia: checked,
      comentario: texto
    })
      .then(() => {
        setSalvo(true)
        if (timerSalvo.current) clearTimeout(timerSalvo.current)
        timerSalvo.current = setTimeout(() => {
          setSalvo(false)
        }, 4000)
      })
      .catch(showNotificationErrorAPI)
      .finally(() => setLoading(false))
  }

  const handleToggle = () => {
    const novoStatus = !possuiPendencia
    setPossuiPendencia(novoStatus)
    setMinimizado(false)
    if (onPendenciaChange) {
      onPendenciaChange(etapa, novoStatus, comentario)
    }
    if (pacoteId) {
      triggerSave(novoStatus, comentario)
    }
  }

  const handleComentarioChange = (e: any) => {
    const texto = e.target.value
    setComentario(texto)
    setSalvo(false)
    if (onPendenciaChange) {
      onPendenciaChange(etapa, possuiPendencia, texto)
    }
    if (pacoteId) {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        triggerSave(possuiPendencia, texto)
      }, 800)
    }
  }

  return (
    <Box
      style={{
        position: 'absolute',
        alignSelf: 'flex-start',
        right: 20,
        top: 80,
        zIndex: 10,
        transition: 'all 0.25s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
      }}
    >
      <Collapse in={possuiPendencia && minimizado} timeout={300} unmountOnExit>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setMinimizado(false)}
          style={{
            backgroundColor: '#fff8f6',
            color: '#d32f2f',
            borderRadius: 20,
            textTransform: 'none',
            padding: '6px 16px',
            border: '1px solid #ffcdd2',
            fontSize: '0.85rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'all 0.25s ease-in-out'
          }}
        >
          Pendência ativa (Expandir)
        </Button>
      </Collapse>
      <Collapse in={possuiPendencia && !minimizado} timeout={300} unmountOnExit>
        <Paper
          elevation={3}
          style={{
            padding: '14px 18px',
            borderRadius: 8,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            width: 440,
            maxWidth: 'calc(100vw - 40px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            transition: 'all 0.25s ease-in-out'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#333333' }}>
              Pendência nesta etapa
            </Typography>
            <Box display="flex" alignItems="center">
              <Button
                size="small"
                onClick={handleToggle}
                style={{
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  color: '#d32f2f',
                  minWidth: 'auto',
                  padding: '2px 8px',
                  marginRight: 6,
                  backgroundColor: 'transparent'
                }}
              >
                Remover pendência
              </Button>
              <Tooltip title="Minimizar" arrow>
                <IconButton
                  size="small"
                  onClick={() => setMinimizado(true)}
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 4,
                    color: '#555555',
                    backgroundColor: '#f5f5f5',
                    padding: 0
                  }}
                >
                  <FiMinus size={15} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            size="small"
            placeholder="Descreva o motivo da pendência..."
            value={comentario}
            onChange={handleComentarioChange}
            InputLabelProps={{ shrink: true }}
            autoFocus
          />
          {loading && (
            <Box display="flex" alignItems="center" mt={1}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 6, fontSize: '0.75rem' }}>
                Salvando pendência...
              </Typography>
            </Box>
          )}
          {salvo && !loading && (
            <Box display="flex" alignItems="center" mt={1}>
              <Typography variant="caption" style={{ color: '#2e7d32', fontSize: '0.75rem', fontWeight: 600 }}>
                ✓ Pendência salva com sucesso!
              </Typography>
            </Box>
          )}
        </Paper>
      </Collapse>

      <Collapse in={!possuiPendencia} timeout={300} unmountOnExit>
        <Button
          variant="outlined"
          size="small"
          onClick={handleToggle}
          style={{
            backgroundColor: '#ffffff',
            color: '#424242',
            borderRadius: 20,
            textTransform: 'none',
            padding: '6px 16px',
            border: '1px solid #dcdcdc',
            fontSize: '0.85rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'all 0.25s ease-in-out'
          }}
        >
          + Marcar pendência nesta etapa
        </Button>
      </Collapse>
    </Box>
  )
}

export const StepContentSyn = ({
  noMarginTop,
  children,
  textoTitulo,
  handleBackToStep,
  handleNext,
  hideBack,
  showFinish,
  disabledButton,
  show,
  loading,
  handleFinish,
  textFinish,
  activeStep,
  lastStep,
  large,
  hideNext,
  isUpdate,
  pendencias,
  onPendenciaChange
}: any) => {
  const classes = useStyles()
  const deveMostrar = !show && {
    style: { display: 'none' }
  }
  const query = useQueryParams()

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [ehEdicaoHospital, setEhEdicaoHospital] = useState(false)
  const [loadingEditar, setLoadingEditar] = useState(false)

  const { perfilAtual } = useAuth()
  const podeMarcarPendencias = [
    SynPerfis.OPERACIONAL,
    SynPerfis.SUPER_ADMIN,
    SynPerfis.ADMIN_HOSPITAL,
    SynPerfis.OPERACIONAL_HOSPITAL
  ].includes(perfilAtual)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [show])
  useEffect(() => {
    verificarEhEdicaoHospital()
  }, [])

  function verificarEhEdicaoHospital() {
    const verificar = query.get('editarHospital')

    if (verificar === 'true') {
      setEhEdicaoHospital(true)
    }
  }
  const handleSalvar = () => {
    setLoadingEditar(true)
    handleFinish()
  }
  return (
    <Container
      maxWidth={large ? 'md' : 'sm'}
      {...deveMostrar}
      className={`fadeInTransition ${classes.stepContent} ${
        !!noMarginTop ? classes.noMarginTop : ''
      }`}
    >
      {!!activeStep && podeMarcarPendencias && (
        <PendenciasBox
          pacoteId={query.get('idPacote')}
          etapa={activeStep}
          pendenciasOriginal={pendencias}
          onPendenciaChange={onPendenciaChange}
        />
      )}

      {!!textoTitulo && (
        <Typography variant="h6" style={{ marginBottom: 16 }}>
          {textoTitulo}
        </Typography>
      )}

      <Grid>
        <Box pb={showFinish ? 6 : 0}>{children}</Box>
      </Grid>

      <Grid
        container
        className={classes.buttons}
        style={{
          marginTop: 16
        }}
      >
        {!!showFinish ? (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Button
                onClick={handleSalvar}
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                loading={ehEdicaoHospital ? loadingEditar : loading}
                disabled={loading || !!disabledButton}
                id="button-salvar-stepper"
              >
                {!!textFinish ? textFinish : 'Salvar'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              {!hideBack && (
                <Button
                  type="button"
                  fullWidth
                  variant="text"
                  color="default"
                  onClick={() => handleBackToStep()}
                  id="button-voltar-stepper"
                >
                  Voltar
                </Button>
              )}
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid item xs={4}>
              {!hideBack && (
                <>
                  {fullScreen ? (
                    <Button
                      type="button"
                      fullWidth
                      variant="text"
                      color="default"
                      onClick={() => handleBackToStep()}
                      id="button-voltar-stepper-mobile"
                    >
                      Voltar
                    </Button>
                  ) : (
                    <Box
                      display="flex"
                      className={classes.cursorPointer}
                      alignItems="center"
                      onClick={() => handleBackToStep()}
                      id="button-voltar-mobile"
                    >
                      <Box mr={2} component={GrPrevious} /> VOLTAR
                    </Box>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={3} style={{ display: 'flex' }}></Grid>
            <Grid
              item
              xs={5}
              container
              justify="flex-end"
              style={{ display: 'flex' }}
            >
              {!hideNext && !ehEdicaoHospital && (
                <>
                  {fullScreen ? (
                    <Button
                      variant="contained"
                      type="button"
                      fullWidth
                      color="primary"
                      onClick={handleNext}
                      id="button-continuar-stepper-mobile"
                    >
                      Continuar
                    </Button>
                  ) : (
                    <Box
                      display="flex"
                      className={classes.cursorPointer}
                      alignItems="center"
                      onClick={handleNext}
                      id="button-continuar-mobile"
                    >
                      CONTINUAR
                      <Box ml={2} component={GrNext} />
                    </Box>
                    //     <Box>
                    //   <UBox render={isUpdate}>
                    //     <Button
                    //       onClick={handleSalvar}
                    //       type="button"
                    //       fullWidth
                    //       variant="contained"
                    //       color="primary"
                    //       loading={ehEdicaoHospital ? loadingEditar : loading}
                    //       disabled={loading || !!disabledButton}
                    //       id="button-salvar-stepper"
                    //     >
                    //       {!!textFinish ? textFinish : 'Salvar'}
                    //     </Button>
                    //   </UBox>
                    // </Box>
                  )}
                </>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  )
}
